import { useCallback, useEffect, useState } from "react";
import { TicketDetailsSidebarState, TicketDetailsTabId } from "../../types";

export const TICKET_ID_SEARCH_PARAM_KEY = "ticket_id";

const getTicketIdFromUrl = (): string | null =>
  new URLSearchParams(window.location.search).get(TICKET_ID_SEARCH_PARAM_KEY);

const setTicketIdInUrl = (ticketId: string | undefined): void => {
  const url = new URL(window.location.href);

  if (ticketId) {
    url.searchParams.set(TICKET_ID_SEARCH_PARAM_KEY, ticketId);
  } else {
    url.searchParams.delete(TICKET_ID_SEARCH_PARAM_KEY);
  }

  window.history.replaceState(window.history.state, "", url);
};

export const useTicketDetailsSidebarState = () => {
  const [state, setState] = useState<TicketDetailsSidebarState>({
    isOpen: false,
    ticketId: "",
  });

  const updateTicketIdInUrl = useCallback((ticketId: string | undefined) => {
    setTicketIdInUrl(ticketId);
  }, []);

  const open = useCallback(
    (ticketId: string, initialTabId?: TicketDetailsTabId) => {
      setState({ isOpen: true, ticketId, initialTabId });
      updateTicketIdInUrl(ticketId);
    },
    [updateTicketIdInUrl]
  );

  const close = useCallback(() => {
    if (state === null) {
      return;
    }

    setState((prev) => {
      if (prev === null) {
        return prev;
      }

      return { ...prev, isOpen: false };
    });

    updateTicketIdInUrl(undefined);
  }, [updateTicketIdInUrl, state]);

  // Restore the ticket ID from the URL when the component mounts
  useEffect(() => {
    const ticketIdFromUrl = getTicketIdFromUrl();

    if (ticketIdFromUrl) {
      setState({ isOpen: true, ticketId: ticketIdFromUrl });
    }
  }, []);

  return {
    ticketDetailsSidebarState: state,
    openTicketDetailsSidebar: open,
    closeTicketDetailsSidebar: close,
  };
};
